import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import type { AvisoDaSala } from '@orbind/core'
import { MenuDeAparelhos, useNivelDoMicrofone } from '../features/chat/controles-de-midia.tsx'
import { CanalDoConvidado } from '../features/chat/canal-do-convidado.ts'
import { aplicarSaida, restricaoDeAudio, restricaoDeVideo } from '../features/chat/dispositivos.ts'
import { MalhaDaSala, type ParticipanteAoVivo } from '../features/chat/sala-mesh.ts'
import { apiOrigin } from '../lib/api.ts'
import '../features/chat/videochamada.css'
import './entrar-na-chamada.css'

/**
 * Entrar numa chamada pelo LINK, sem conta (SPEC-20260917-0047).
 *
 * Fora do shell autenticado de propósito: quem abre esta página é alguém de
 * fora da empresa. A tela é a porta — a pessoa se vê, ajusta o que vai mostrar,
 * diz quem é, e espera alguém de dentro deixar entrar.
 *
 * A ordem importa: a mídia é preparada ANTES de bater na porta, para o tempo de
 * espera não ser gasto procurando qual microfone é qual.
 */

type Fase = 'carregando' | 'invalida' | 'porta' | 'esperando' | 'recusado' | 'dentro' | 'encerrada'

type Convite = { anfitriao: string; assunto: string | null }

export function EntrarNaChamadaPage() {
  const { token = '' } = useParams()
  const [fase, setFase] = useState<Fase>('carregando')
  const [convite, setConvite] = useState<Convite | null>(null)
  const [nome, setNome] = useState('')
  const [recado, setRecado] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [micLigado, setMicLigado] = useState(true)
  const [camLigada, setCamLigada] = useState(true)
  const [participantes, setParticipantes] = useState<ParticipanteAoVivo[]>([])
  const [menuAparelhos, setMenuAparelhos] = useState<HTMLElement | null>(null)

  const previa = useRef<HTMLVideoElement>(null)
  const microfone = useRef<MediaStreamTrack | null>(null)
  const camera = useRef<MediaStreamTrack | null>(null)
  const canal = useRef<CanalDoConvidado | null>(null)
  const malha = useRef<MalhaDaSala | null>(null)
  const [local, setLocal] = useState<MediaStream | null>(null)
  const nivel = useNivelDoMicrofone(micLigado ? microfone.current : null)

  const base = useMemo(() => `${apiOrigin()}/public/call-invite/${encodeURIComponent(token)}`, [token])

  /* ------------------------------------------- o convite vale? --- */

  useEffect(() => {
    let vivo = true
    fetch(base)
      .then((r) => r.json() as Promise<{ aberta: boolean; anfitriao?: string; assunto?: string | null }>)
      .then((dados) => {
        if (!vivo) return
        if (!dados.aberta) {
          setFase('invalida')
          return
        }
        setConvite({ anfitriao: dados.anfitriao ?? '', assunto: dados.assunto ?? null })
        setFase('porta')
      })
      .catch(() => vivo && setFase('invalida'))
    return () => {
      vivo = false
    }
  }, [base])

  /* ------------------------------------------------- a minha mídia --- */

  const atualizarPrevia = useCallback(() => {
    const faixas = [microfone.current, camera.current].filter((t): t is MediaStreamTrack => t !== null)
    setLocal(faixas.length > 0 ? new MediaStream(faixas) : null)
  }, [])

  useEffect(() => {
    if (fase !== 'porta') return
    let vivo = true
    navigator.mediaDevices
      ?.getUserMedia({ audio: restricaoDeAudio(), video: restricaoDeVideo() })
      .then((midia) => {
        if (!vivo) {
          midia.getTracks().forEach((t) => t.stop())
          return
        }
        microfone.current = midia.getAudioTracks()[0] ?? null
        camera.current = midia.getVideoTracks()[0] ?? null
        setCamLigada(camera.current !== null)
        atualizarPrevia()
      })
      .catch(() => {
        if (!vivo) return
        // Sem câmera ou microfone ainda dá para entrar — só entra sem eles.
        setCamLigada(false)
        setErro('Não foi possível usar a câmera ou o microfone. Você pode entrar mesmo assim.')
      })
    return () => {
      vivo = false
    }
    // Só quando a porta abre: reexecutar aqui apagaria a mídia no meio da chamada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase === 'porta'])

  useEffect(() => {
    if (previa.current) previa.current.srcObject = local
  }, [local, fase])

  // Sair da página solta câmera e microfone — a luz do aparelho tem que apagar.
  useEffect(() => {
    return () => {
      microfone.current?.stop()
      camera.current?.stop()
      malha.current?.encerrar()
      canal.current?.encerrar()
    }
  }, [])

  const alternarMic = () => {
    const ligado = !micLigado
    if (microfone.current) microfone.current.enabled = ligado
    setMicLigado(ligado)
  }

  const alternarCam = async () => {
    if (camera.current) {
      camera.current.stop()
      camera.current = null
      setCamLigada(false)
      atualizarPrevia()
      await malha.current?.atualizarMidia()
      return
    }
    try {
      const midia = await navigator.mediaDevices.getUserMedia({ video: restricaoDeVideo() })
      camera.current = midia.getVideoTracks()[0] ?? null
      setCamLigada(camera.current !== null)
      atualizarPrevia()
      await malha.current?.atualizarMidia()
    } catch {
      setErro('Não foi possível ligar a câmera.')
    }
  }

  const trocarAparelho = async (tipo: 'microfone' | 'camera') => {
    try {
      const midia = await navigator.mediaDevices.getUserMedia(
        tipo === 'microfone' ? { audio: restricaoDeAudio() } : { video: restricaoDeVideo() },
      )
      if (tipo === 'microfone') {
        const nova = midia.getAudioTracks()[0] ?? null
        if (!nova) return
        nova.enabled = micLigado
        microfone.current?.stop()
        microfone.current = nova
      } else {
        const nova = midia.getVideoTracks()[0] ?? null
        if (!nova) return
        camera.current?.stop()
        camera.current = nova
        setCamLigada(true)
      }
      atualizarPrevia()
      await malha.current?.atualizarMidia()
    } catch {
      setErro(`Não foi possível usar esse ${tipo === 'microfone' ? 'microfone' : 'câmera'}.`)
    }
  }

  /* ----------------------------------------------- bater na porta --- */

  const pedirParaEntrar = async () => {
    const meuNome = nome.trim()
    if (meuNome === '') {
      setErro('Diga seu nome para quem está na chamada saber quem é.')
      return
    }
    setEnviando(true)
    setErro(null)
    try {
      const resposta = await fetch(`${base}/knock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: meuNome, recado: recado.trim() || null }),
      })
      if (!resposta.ok) throw new Error('recusado')
      const { guestToken, roomId } = (await resposta.json()) as { guestToken: string; roomId: string }
      setFase('esperando')
      abrirCanal(guestToken, roomId)
    } catch {
      setErro('Esta chamada não está mais disponível.')
      setFase('invalida')
    } finally {
      setEnviando(false)
    }
  }

  const abrirCanal = (guestToken: string, roomId: string) => {
    const conexao = new CanalDoConvidado()
    canal.current = conexao
    const minhaMalha = new MalhaDaSala({
      enviar: (para, sinal) => conexao.enviarSinal(para, sinal),
      aoMudar: setParticipantes,
      fontes: {
        audio: () => microfone.current,
        video: () => camera.current,
        tela: () => null,
      },
    })
    malha.current = minhaMalha

    conexao.conectar(guestToken, (evento: AvisoDaSala) => {
      if (evento.evento.nome === 'decisao') {
        if (!evento.evento.aprovado) {
          setFase('recusado')
          return
        }
        setFase('dentro')
        conexao.entrarNaSala(roomId)
        return
      }
      if (evento.evento.nome === 'encerrada') {
        setFase('encerrada')
        minhaMalha.encerrar()
        return
      }
      void minhaMalha.tratarEvento(evento.evento)
      if (evento.evento.nome === 'entrei' || evento.evento.nome === 'entrou') {
        minhaMalha.anunciarMidia(camera.current !== null, false)
      }
    })
  }

  /* ------------------------------------------------------ telas --- */

  if (fase === 'carregando') {
    return (
      <div className="ec" role="status">
        <p className="ec__aviso">Abrindo o convite…</p>
      </div>
    )
  }

  if (fase === 'invalida') {
    return (
      <div className="ec">
        <div className="ec__cartao">
          <i className="fas fa-door-closed ec__icone" aria-hidden />
          <h1 className="ec__titulo">Esta chamada não está disponível</h1>
          <p className="ec__texto">
            O link pode ter expirado ou a chamada já terminou. Peça um novo link para quem convidou você.
          </p>
        </div>
      </div>
    )
  }

  if (fase === 'recusado') {
    return (
      <div className="ec">
        <div className="ec__cartao">
          <i className="fas fa-hand-paper ec__icone" aria-hidden />
          <h1 className="ec__titulo">Entrada não liberada</h1>
          <p className="ec__texto">Quem está na chamada não liberou sua entrada desta vez.</p>
        </div>
      </div>
    )
  }

  if (fase === 'encerrada') {
    return (
      <div className="ec">
        <div className="ec__cartao">
          <i className="fas fa-phone-slash ec__icone" aria-hidden />
          <h1 className="ec__titulo">A chamada terminou</h1>
          <p className="ec__texto">Obrigado por participar.</p>
        </div>
      </div>
    )
  }

  if (fase === 'dentro') {
    return <SalaDoConvidado participantes={participantes} local={local} micLigado={micLigado} camLigada={camLigada} onMic={alternarMic} onCam={() => void alternarCam()} onSair={() => { canal.current?.sair(); malha.current?.encerrar(); setFase('encerrada') }} />
  }

  // porta e espera compartilham a prévia: a pessoa continua se vendo enquanto
  // aguarda, e pode corrigir o enquadramento antes de aparecer para os outros.
  const esperando = fase === 'esperando'
  return (
    <div className="ec ec--porta">
      <div className="ec__palco">
        <video
          ref={previa}
          className={`ec__video${camLigada ? '' : ' ec__video--off'}`}
          autoPlay
          playsInline
          muted
        />
        {camLigada ? null : (
          <div className="ec__semcam">
            <i className="fas fa-video-slash" aria-hidden />
            <p>Câmera desligada</p>
          </div>
        )}
        <div className="ec__controles">
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${micLigado ? '' : ' vc-botao--desligado'}`}
            aria-label={micLigado ? 'Desligar o microfone' : 'Ligar o microfone'}
            title={micLigado ? 'Desligar o microfone' : 'Ligar o microfone'}
            onClick={alternarMic}
          >
            <i className={micLigado ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden />
            {micLigado ? (
              <span className="vc-botao__pulso" style={{ transform: `scale(${1 + nivel * 0.5})` }} aria-hidden />
            ) : null}
          </button>
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${camLigada ? '' : ' vc-botao--desligado'}`}
            aria-label={camLigada ? 'Desligar a câmera' : 'Ligar a câmera'}
            title={camLigada ? 'Desligar a câmera' : 'Ligar a câmera'}
            onClick={() => void alternarCam()}
          >
            <i className={camLigada ? 'fas fa-video' : 'fas fa-video-slash'} aria-hidden />
          </button>
          <button
            type="button"
            className="vc-botao vc-botao--redondo"
            aria-haspopup="dialog"
            aria-label="Escolher e testar microfone, câmera e som"
            title="Aparelhos e testes"
            onClick={(e) => {
              const alvo = e.currentTarget
              setMenuAparelhos((atual) => (atual ? null : alvo))
            }}
          >
            <i className="fas fa-sliders-h" aria-hidden />
          </button>
          {menuAparelhos ? (
            <MenuDeAparelhos
              ancora={menuAparelhos}
              comTeste
              faixaDoMicrofone={micLigado ? microfone.current : null}
              onFechar={() => setMenuAparelhos(null)}
              onTrocarMicrofone={() => void trocarAparelho('microfone')}
              onTrocarCamera={() => void trocarAparelho('camera')}
              onTrocarSaida={() => void aplicarSaida(previa.current)}
            />
          ) : null}
        </div>
      </div>

      <aside className="ec__painel">
        {esperando ? (
          <>
            <h1 className="ec__titulo">Esperando alguém deixar você entrar</h1>
            <p className="ec__texto">
              {convite?.anfitriao ? `${convite.anfitriao} já foi avisado.` : 'Quem está na chamada já foi avisado.'} Não
              feche esta página.
            </p>
            <div className="ec__batendo" aria-hidden>
              <span />
              <span />
              <span />
            </div>
          </>
        ) : (
          <>
            <h1 className="ec__titulo">Pedir para entrar</h1>
            <p className="ec__texto">
              {convite?.anfitriao ? <strong>{convite.anfitriao}</strong> : 'Alguém'} convidou você
              {convite?.assunto ? ` para “${convite.assunto}”` : ''}.
            </p>

            <label className="ec__campo">
              <span className="ec__rotulo">Seu nome</span>
              <input
                className="ec__input"
                value={nome}
                maxLength={80}
                autoFocus
                placeholder="Como quer aparecer na chamada"
                onChange={(e) => setNome(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void pedirParaEntrar()
                }}
              />
            </label>

            <label className="ec__campo">
              <span className="ec__rotulo">
                Recado <span className="ec__opcional">(opcional)</span>
              </span>
              <textarea
                className="ec__input ec__input--area"
                value={recado}
                maxLength={280}
                rows={2}
                placeholder="De onde você fala, ou o motivo — ajuda quem vai te deixar entrar"
                onChange={(e) => setRecado(e.target.value)}
              />
            </label>

            <button type="button" className="ec__entrar" disabled={enviando} onClick={() => void pedirParaEntrar()}>
              <i className="fas fa-door-open" aria-hidden /> {enviando ? 'Pedindo…' : 'Pedir para entrar'}
            </button>
          </>
        )}
        {erro ? <p className="ec__erro">{erro}</p> : null}
      </aside>
    </div>
  )
}

/** A chamada, do lado de quem entrou pelo link. */
export function SalaDoConvidado({
  participantes,
  local,
  micLigado,
  camLigada,
  onMic,
  onCam,
  onSair,
  vazio = 'Conectando com quem está na chamada…',
}: {
  /** O que dizer enquanto não há mais ninguém na grade. */
  vazio?: string
  participantes: ParticipanteAoVivo[]
  local: MediaStream | null
  micLigado: boolean
  camLigada: boolean
  onMic: () => void
  onCam: () => void
  onSair: () => void
}) {
  const minha = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    if (minha.current) minha.current.srcObject = local
  }, [local])

  return (
    <div className="ec ec--dentro">
      <div className="ec__grade">
        {participantes.length === 0 ? (
          <p className="ec__aviso">{vazio}</p>
        ) : (
          participantes.map((p) => <Remoto key={p.id} participante={p} />)
        )}
      </div>
      <video ref={minha} className="ec__minha" autoPlay playsInline muted />
      <div className="ec__controles ec__controles--fixos">
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${micLigado ? '' : ' vc-botao--desligado'}`}
          aria-label={micLigado ? 'Desligar o microfone' : 'Ligar o microfone'}
          onClick={onMic}
        >
          <i className={micLigado ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden />
        </button>
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${camLigada ? '' : ' vc-botao--desligado'}`}
          aria-label={camLigada ? 'Desligar a câmera' : 'Ligar a câmera'}
          onClick={onCam}
        >
          <i className={camLigada ? 'fas fa-video' : 'fas fa-video-slash'} aria-hidden />
        </button>
        <button
          type="button"
          className="vc-botao vc-botao--redondo vc-botao--recusar"
          aria-label="Sair da chamada"
          onClick={onSair}
        >
          <i className="fas fa-phone-slash" aria-hidden />
        </button>
      </div>
    </div>
  )
}

export function Remoto({ participante }: { participante: ParticipanteAoVivo }) {
  const camera = useRef<HTMLVideoElement>(null)
  const tela = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (camera.current) camera.current.srcObject = participante.camera
  }, [participante.camera])
  useEffect(() => {
    if (tela.current) tela.current.srcObject = participante.tela
  }, [participante.tela])

  return (
    <div className="ec__pessoa">
      {participante.midia.tela ? <video ref={tela} className="ec__remoto ec__remoto--tela" autoPlay playsInline /> : null}
      <video
        ref={camera}
        className={`ec__remoto${participante.midia.video ? '' : ' ec__remoto--off'}`}
        autoPlay
        playsInline
      />
      {participante.midia.video ? null : (
        <span className="ec__sonome" aria-hidden>
          {participante.nome}
        </span>
      )}
      <span className="ec__etiqueta">{participante.nome}</span>
    </div>
  )
}
